// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * Shared helpers for components that call the backend directly with fetch
 * (outside the React Admin dataProvider). Centralizing the token key and
 * Bearer scheme means an auth change (storage key, refresh wrapper, cookie
 * migration) lands in ONE place instead of a per-component hunt.
 */

export const authHeaders = (): Record<string, string> => {
  // In a fresh tab sessionStorage is empty but the HttpOnly payload-token cookie
  // still authenticates the session (see authProvider's credentials:'include').
  // Emitting `Bearer null` in that case is misleading log-noise; omit the header
  // entirely so the same-origin cookie carries auth cleanly.
  const token = sessionStorage.getItem('token')
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json',
  }
}

/** Lenient client-side email shape check (server revalidates). */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Extract a human-readable error message from a non-ok fetch Response.
 * Payload's own validation errors come back as `{errors:[{message}]}`, not
 * `{message}` — a caller that reads only `body.message` always discards the
 * server's actual reason and shows a generic toast instead (P71-6). Also
 * covers `{error}` (the shape this repo's own custom endpoints use).
 *
 * `fallback` is what to return when the body can't be parsed as JSON at all
 * (or carries none of the known message fields) — pass a translated string
 * so a malformed/non-JSON error body still shows the app's normal generic
 * message instead of a raw "HTTP 500".
 */
export const errorMessageOf = async (res: Response, fallback?: string): Promise<string> => {
  try {
    const body = await res.json()
    return (
      body?.error || body?.message || body?.errors?.[0]?.message || fallback || `HTTP ${res.status}`
    )
  } catch (_e) {
    return fallback || `HTTP ${res.status}`
  }
}
